/**
 * Calling the v2 API's internal endpoints (`/api/v2/internal/*`) from the web app.
 *
 * Internal endpoints are the web app's own (restServer `apiv2/internal/`, ADR 0039):
 * undocumented, so their shapes can change with the app; OAuth only, so never an
 * API key; and, unless an endpoint says otherwise, answered only for a registered
 * RunDiffusion web browser. That last rule is why every call carries this
 * browser's `web_app` Connected Device id as `X-Device-Id` (`webClientDevice.ts`)
 * as well as the Firebase ID token. Without it the server answers 403
 * `WEB_APP_DEVICE_REQUIRED`.
 *
 * A public endpoint (`/api/v2/teams`, `/api/v2/members`) needs none of this: its
 * clients send the token alone. When to build which, and how: restServer's
 * `apiv2/README.md`.
 *
 * This module is pure (paths and headers), so the clients that use it stay
 * Jest-testable. The credentials come from `services/internalApiCredentials.ts`:
 *
 *   const credentials = await internalApiCredentials();
 *   const result = credentials
 *     ? await fetchTeamsSummary({ baseUrl: TEAMS_API_BASE_URL, ...credentials, request })
 *     : { ok: false, reason: 'unauthorized' };
 *   const failure = teamsSummaryFailure(result);
 *   if (failure) forgetWebClientDeviceIfRejected(failure.code);
 */

export interface InternalApiCredentials {
	idToken: string;
	// This browser's `web_app` Connected Device, sent as `X-Device-Id`.
	deviceId: string;
}

/** Where an internal path goes: `internalApiPath('/teams/summary')` is `/internal/teams/summary`. */
export function internalApiPath (path: string): string {
	return `/internal/${path.replace(/^\/+/, '')}`;
}

/** The headers every internal request carries: the token, and the browser's web client device. */
export function internalApiHeaders (credentials: InternalApiCredentials): Record<string, string> {
	return {
		'Authorization': `Bearer ${credentials.idToken}`,
		'X-Device-Id': credentials.deviceId,
	};
}
